import { Box, Paper, Typography, } from "@mui/material";
import moment from "moment";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import DateRangePickerComponent from "../../../core/components/common/custom-date-range-picker.component";
import EmptyBox from "../../../assets/icons/empty-box.png";
import { endOfWeek, format, parseISO, startOfDay, startOfHour, startOfWeek } from "date-fns";
import { useEffect, useState } from "react";

export default function AccessLogsAnalyticsComponent (props) {

    const {startDate, endDate, handleDateRangeChange, accessLogs} = props;

    const [logViewMode, setLogViewMode] = useState("weekly");
    const [filteredGraphData, setFilteredGraphData] = useState([])

    const formatLabel = (timestamp, mode, startDate, endDate) => {
    const date = parseISO(timestamp);

    switch (mode) {
      case "hourly":
        return format(startOfHour(date), "MMM d, HH:00");

      case "daily":
        return format(startOfDay(date), "MMM d");

      case "weekly": {
        let weekStart = startOfWeek(date, { weekStartsOn: 1 });
        let weekEnd = endOfWeek(date, { weekStartsOn: 1 });

        // Clip weekStart to startDate
        if (weekStart < startDate.toDate()) {
          weekStart = startDate.toDate();
        }
        // Clip weekEnd to endDate (optional)
        if (endDate && weekEnd > endDate.toDate()) {
          weekEnd = endDate.toDate();
        }

        return `${format(weekStart, "MMM d")}–${format(weekEnd, "MMM d")}`;
      }

      case "monthly": {
        let weekStart = startOfWeek(date, { weekStartsOn: 1 });
        let weekEnd = endOfWeek(date, { weekStartsOn: 1 });

        // Clip weekStart to startDate
        if (weekStart < startDate.toDate()) {
          weekStart = startDate.toDate();
        }
        if (endDate && weekEnd > endDate.toDate()) {
          weekEnd = endDate.toDate();
        }

        return `${format(weekStart, "MMM d")}–${format(weekEnd, "MMM d")}`;
      }

      default:
        return timestamp;
    }
  };

  const groupAccessLogs = (logs, mode) => {
    const grouped = {};
    const selectedMonth = startDate.month(); // 0-indexed

    logs?.forEach(([timestamp, type, count]) => {
      const momentDate = moment(timestamp);

      if (!momentDate.isBetween(startDate.startOf('day'), endDate.endOf('day'), null, '[]')) {
        return;
      }


      const label = formatLabel(timestamp, mode, startDate, endDate);

      if (mode === "monthly") {
        const weekStart = moment(startOfWeek(momentDate.toDate(), { weekStartsOn: 1 }));
        const weekEnd = moment(endOfWeek(momentDate.toDate(), { weekStartsOn: 1 }));

        if (weekStart.month() !== selectedMonth && weekEnd.month() !== selectedMonth) return;
      }

      if (!grouped[label]) grouped[label] = { name: label, IN: 0, OUT: 0 };
      grouped[label][type] += count;
    });

    return Object.values(grouped);
  };

  useEffect(() => {
    if (accessLogs && accessLogs.length > 0) {
      const newData = groupAccessLogs(accessLogs, logViewMode);
      setFilteredGraphData(newData);
    } else {
      setFilteredGraphData([]);
    }
  }, [accessLogs, logViewMode, startDate, endDate]);

    return (
        <Box>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>Access Logs</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <DateRangePickerComponent
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                  ranges={{
                    "Today": [moment().startOf('day'), moment().endOf('day')],
                    "Last 7 Days": [moment().subtract(6, "days").startOf('day'), moment().endOf('day')],
                    "This Month": [moment().startOf("month"), moment().endOf("month")],
                    "Last Month": [
                      moment().subtract(1, "month").startOf("month"),
                      moment().subtract(1, "month").endOf("month"),
                    ],
                    "Last 90 Days": [moment().subtract(89, "days").startOf('day'), moment().endOf('day')],
                  }}
                  onDateRangeChange={handleDateRangeChange}
                />
              </Box>

              {accessLogs?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredGraphData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="IN" fill="#1976d2" />
                    <Bar dataKey="OUT" fill="#dc004e" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    minHeight: "300px",
                    textAlign: "center",
                  }}
                >
                  <img src={EmptyBox} alt="No Data" width={120} />
                  <Typography variant="subtitle1" color="textSecondary" mt={2}>
                    No Data Available
                  </Typography>
                </Box>
              )}
            </Paper>
        </Box>
    )
}